/**
 * グローバルナビゲーションが扱うホストアプリの識別子。
 * - karte-io: KARTE 本体 (admin.karte.io)
 * - qant: Qant 専用ドメイン (app.qant.jp)
 */
export type NaviHost = 'karte-io' | 'qant';

export const NAVI_DOMAINS = {
  'karte-io': 'admin.karte.io',
  qant: 'app.qant.jp',
} as const satisfies { [K in NaviHost]: string };

/**
 * ナビゲーションリンクのターゲット。
 * `path` は host 上の相対パス（例: '/rightsupport/'）。
 * 実際の URL 解決はホストアプリ側で `resolveNaviUrl` を通して行う。
 */
export type NaviTarget = {
  host: NaviHost;
  path: string;
  /**
   * true のとき、`createHrefBuilder` が付与するプロジェクト指定クエリ (`?pid=` / `?project=`) を付けない。
   * auth 設定ページ (アカウント設定・ログアウト・組織/プロジェクト設定) など、パスパラメータで
   * 対象が確定していてプロジェクトクエリが不要なリンクに使う。
   */
  skipProjectParam?: boolean;
  /**
   * プロジェクトの指定方法。既定 (未指定) はクエリ (`?project=` / `?pid=`)。
   * `'path'` を指定すると `/p/<projectId>` をパスに前置し、クエリは付けない。
   *
   * KARTE 本体の機能ページ (イベント設定・セグメントなど) は `?project=` を解釈せず
   * `/p/<projectId>/...` のみを受け付けるため、そのリンクに使う
   * (KARTE 現行ナビの `getProjectUrl` / `getAdminProjectUrl` の使い分けに対応)。
   */
  projectIdIn?: 'query' | 'path';
};

export type ResolvedNaviUrl = {
  /**
   * 同一 host のとき: target.path をそのまま返す（host アプリのプロジェクト URL ヘルパで包むことを想定）。
   * 別 host のとき: 絶対 URL（https://<domain><path>）を返す。
   */
  href: string;
  /**
   * target host と現在の hostApp が異なる場合 true。
   * UI 側で target=_blank などのクロスドメイン用ハンドリングに利用する。
   */
  crossDomain: boolean;
};

/**
 * ナビゲーション target を、現在動作している hostApp の文脈で解決する。
 *
 * 同一 host の場合は path をそのまま返し、ホストアプリ側の getProjectUrl 等の
 * ヘルパで最終 URL に展開することを期待する。
 * クロスドメインの場合は完全な絶対 URL を返す。
 *
 * `originOverride` を渡すと、その origin (scheme 込み, 例 `https://app.qant.jp`) を
 * 遷移先として使う。env (dev/stg/prod) ごとに正しいドメインを backend から渡す用途。
 * 省略時は prod の `NAVI_DOMAINS` を `https://` 付きで使う (後方互換)。
 */
export function resolveNaviUrl(target: NaviTarget, hostApp: NaviHost, originOverride?: string): ResolvedNaviUrl {
  const crossDomain = target.host !== hostApp;
  if (!crossDomain) {
    return { href: target.path, crossDomain: false };
  }
  const base = originOverride ?? `https://${NAVI_DOMAINS[target.host]}`;
  return {
    href: `${base}${target.path}`,
    crossDomain: true,
  };
}
